import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatDateTime, formatMoney } from '@tabita/i18n';
import { useI18n } from '@/i18n/i18n-provider';
import { givingApi } from '@/services/giving.api';
import { Button, EmptyState, Field, Spinner, StatusBadge } from '@/components/ui';

/**
 * The reference's "Donación" tab, built for real — Giving already supports
 * `donorPersonId`, so this is a pure frontend read against an existing,
 * working endpoint. The total is summed from the loaded page only (there's
 * no aggregate total endpoint); "Enviar por correo" has no backend to send
 * a statement, so it stays disabled rather than pretending to work.
 */
export function DonationsTab({ personId }: { readonly personId: string }) {
  const { t, locale } = useI18n();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const query = useQuery({
    queryKey: ['giving', 'donations', { donorPersonId: personId, dateFrom, dateTo }],
    queryFn: ({ signal }) =>
      givingApi.list(
        {
          donorPersonId: personId,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          limit: 100,
        },
        signal,
      ),
  });

  const donations = query.data?.data ?? [];
  const total = donations.reduce((sum, donation) => sum + donation.amount, 0);
  const currency = donations[0]?.currency ?? 'USD';

  function exportCsv(): void {
    const header = ['Date', 'Fund', 'Method', 'Amount', 'Check number'];
    const rows = donations.map((donation) => [
      donation.receivedAt,
      donation.fund,
      donation.method,
      String(donation.amount),
      donation.checkNumber ?? '',
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'donations.csv';
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      <div className="donations-toolbar">
        <Field
          label={t('people.donations.dateFrom')}
          name="dateFrom"
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
        />
        <Field
          label={t('people.donations.dateTo')}
          name="dateTo"
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
        />
        <div className="donations-summary">
          <span className="donations-summary-label">{t('people.donations.totalAmount')}</span>
          <span dir="ltr">{formatMoney(total, currency, locale)}</span>
        </div>
        <div className="donations-toolbar-actions">
          <Button type="button" variant="ghost" onClick={() => window.print()}>
            {t('people.donations.print')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={exportCsv}
            disabled={donations.length === 0}
          >
            {t('common.actions.export')}
          </Button>
          <Button type="button" variant="ghost" disabled>
            {t('people.donations.emailReport')}
          </Button>
        </div>
      </div>

      {query.isPending && <Spinner label={t('common.states.loading')} />}

      {query.isSuccess && donations.length === 0 && (
        <EmptyState title={t('people.donations.empty')} />
      )}

      {donations.length > 0 && (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('giving.donation.receivedAt')}</th>
                <th scope="col">{t('giving.donation.fund')}</th>
                <th scope="col">{t('giving.donation.method')}</th>
                <th scope="col">{t('giving.donation.amount')}</th>
                <th scope="col">{t('giving.donation.checkNumber')}</th>
              </tr>
            </thead>
            <tbody>
              {donations.map((donation) => (
                <tr key={donation.id}>
                  <td dir="ltr">{formatDateTime(donation.receivedAt, locale)}</td>
                  <td>{t(`giving.fund.${donation.fund}`)}</td>
                  <td>
                    {t(`giving.method.${donation.method}`)}
                    {donation.status !== 'RECORDED' && (
                      <StatusBadge
                        status={donation.status}
                        label={t(`giving.status.${donation.status}`)}
                      />
                    )}
                  </td>
                  <td dir="ltr">{formatMoney(donation.amount, donation.currency, locale)}</td>
                  <td dir="ltr">{donation.checkNumber ?? <span className="cell-empty">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
